import type {
  BrowserLiveViewPayload,
  BrowserLiveViewStatus,
  ConversationLiveView,
} from '../types/agent';

const LIVE_VIEW_STATES: BrowserLiveViewStatus[] = ['starting', 'ready', 'closed', 'error'];

function isLiveViewStatus(value: unknown): value is BrowserLiveViewStatus {
  return typeof value === 'string' && LIVE_VIEW_STATES.includes(value as BrowserLiveViewStatus);
}

export function isBrowserLiveViewPayload(value: unknown): value is BrowserLiveViewPayload {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<BrowserLiveViewPayload>;
  return candidate.kind === 'browser_live_view'
    && typeof candidate.live_view_ref === 'string'
    && candidate.live_view_ref.trim().length > 0
    && isLiveViewStatus(candidate.state);
}

export function normalizeConversationLiveView(value: unknown): ConversationLiveView | null {
  if (isBrowserLiveViewPayload(value)) {
    return {
      liveViewRef: value.live_view_ref,
      state: value.state,
      available: value.available === true,
      watchOnly: value.watch_only !== false,
      updatedAt: value.updated_at ?? null,
      errorMessage: value.error_message ?? null,
    };
  }

  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const liveViewRef = typeof candidate.liveViewRef === 'string' && candidate.liveViewRef.trim()
    ? candidate.liveViewRef.trim()
    : null;
  if (!liveViewRef) return null;

  return {
    liveViewRef,
    state: isLiveViewStatus(candidate.state) ? candidate.state : 'ready',
    available: candidate.available === true,
    watchOnly: candidate.watchOnly !== false,
    updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : null,
    errorMessage: typeof candidate.errorMessage === 'string' ? candidate.errorMessage : null,
  };
}

export function serializeConversationLiveView(
  liveView: ConversationLiveView | null | undefined,
): Record<string, unknown> | undefined {
  if (!liveView?.liveViewRef) return undefined;
  return {
    liveViewRef: liveView.liveViewRef,
    state: liveView.state,
    available: liveView.available,
    watchOnly: liveView.watchOnly,
    ...(liveView.updatedAt ? { updatedAt: liveView.updatedAt } : {}),
    ...(liveView.errorMessage ? { errorMessage: liveView.errorMessage } : {}),
  };
}

export function isConversationLiveViewAvailable(liveView: ConversationLiveView | null | undefined): boolean {
  return !!(liveView?.liveViewRef && liveView.available && liveView.state === 'ready');
}
